import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Phone, Check } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedPagesGrid, type RelatedPageCard } from "@/components/RelatedPagesGrid";
import { SCHEMA_LOCALBUSINESS_ID, SITE_URL } from "@/lib/entitySchema";

export type GuideSection = { heading: string; body: string; bullets?: string[] };
export type GuideStep = { title: string; body: string };
export type GuideFAQ = { q: string; a: string };

export type RelocationGuideConfig = {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  breadcrumbs: { label: string; to?: string }[];
  currentLabel: string;
  eyebrow: string;
  h1: ReactNode;
  heroDescription: string;
  intro: string;
  sections: GuideSection[];
  checklist: GuideStep[];
  faqs: GuideFAQ[];
  relatedLinks: { to: string; label: string; description?: string }[];
  datePublished?: string;
};

export function buildRelocationHead(config: RelocationGuideConfig) {
  const selfUrl = `${SITE_URL}/${config.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `${selfUrl}#article`,
        headline: config.metaTitle,
        description: config.metaDescription,
        datePublished: config.datePublished ?? "2026-01-01",
        dateModified: new Date().toISOString().slice(0, 10),
        author: { "@id": SCHEMA_LOCALBUSINESS_ID },
        publisher: { "@id": SCHEMA_LOCALBUSINESS_ID },
        mainEntityOfPage: selfUrl,
      },
      {
        "@type": "HowTo",
        "@id": `${selfUrl}#howto`,
        name: config.metaTitle,
        step: config.checklist.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.body,
        })),
      },
      {
        "@type": "FAQPage",
        "@id": `${selfUrl}#faq`,
        mainEntity: config.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${selfUrl}#breadcrumbs`,
        itemListElement: [
          ...config.breadcrumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.label,
            item: c.to ? `${SITE_URL}${c.to}` : undefined,
          })),
          {
            "@type": "ListItem",
            position: config.breadcrumbs.length + 1,
            name: config.currentLabel,
            item: selfUrl,
          },
        ],
      },
    ],
  };

  return {
    meta: [
      { title: config.metaTitle },
      { name: "description", content: config.metaDescription },
      { property: "og:title", content: config.metaTitle },
      { property: "og:description", content: config.metaDescription },
      { property: "og:url", content: selfUrl },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: selfUrl }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  };
}

export function RelocationGuidePage({ config }: { config: RelocationGuideConfig }) {
  return (
    <SiteLayout>
      <PageHero
        eyebrow={config.eyebrow}
        title={config.h1}
        description={config.heroDescription}
      >
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold hover:-translate-y-0.5"
          >
            Get a Free Proposal <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-md border-2 border-brand-gold/40 bg-card/10 px-6 py-3 text-base font-bold text-brand-gold backdrop-blur hover:border-brand-gold hover:bg-card/20"
            aria-label="Call (719) 419-3935"
          >
            <Phone className="h-4 w-4" /> (719) 419-3935
          </a>
        </div>
      </PageHero>

      <nav aria-label="Breadcrumb" className="border-b border-border bg-muted/30">
        <div className="mx-auto max-w-5xl px-5 py-4 lg:px-8">
          <Breadcrumbs items={[...config.breadcrumbs, { label: config.currentLabel }]} />
        </div>
      </nav>

      <section className="bg-background py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="text-lg leading-relaxed text-foreground/85">{config.intro}</p>
        </div>
      </section>

      <section className="border-y border-border bg-section-alt py-16">
        <div className="mx-auto max-w-3xl space-y-10 px-5 lg:px-8">
          {config.sections.map((s) => (
            <div key={s.heading}>
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                {s.heading}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">{s.body}</p>
              {s.bullets && (
                <ul className="mt-4 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold-deep" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            90-day launch checklist
          </h2>
          <ol className="mt-8 space-y-4">
            {config.checklist.map((step, i) => (
              <li
                key={step.title}
                className="flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-card"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-gold text-base font-black text-brand-black">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-foreground">{step.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/85">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <RelatedPagesGrid
        cards={config.relatedLinks.map<RelatedPageCard>((l) => ({
          to: l.to,
          title: l.label,
          description: l.description ?? `Read more about ${l.label.toLowerCase()}.`,
        }))}
      />

      <section className="bg-background py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">FAQ</h2>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card shadow-card"
          >
            {config.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-0 px-5 sm:px-6">
                <AccordionTrigger className="py-5 text-left text-base font-bold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 pt-0 text-base leading-relaxed text-foreground/85">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Launching in Colorado? Let's build your website right.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Owner-led, hand-coded, conversion-tested. Built in Colorado, for Colorado.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold hover:-translate-y-0.5"
            >
              Get a Free Proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground hover:border-brand-gold/60"
              aria-label="Call (719) 419-3935"
            >
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
